import clsx from 'clsx'
import { Input, InputProps } from 'components/core-v2/atoms'
import { getFieldIds } from 'components/core-v2/molecules/fields/__utils__/getFieldIds'
import { ChangeEvent, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { ClassName } from 'types/props'

import classes from './numerical-display-field.module.scss'

export interface NumericalDisplayValue {
  display: string
  numerical: number
}

export interface NumericalDisplayProps extends InputProps {
  decimalPlaces?: number
  defaultValue?: number
  id: string
  placeholder?: string
  validators?: { validate: { [rule: string]: (value: NumericalDisplayValue) => boolean } }
}

export type NumericalDisplayFieldProps = NumericalDisplayProps & ClassName

export const NumericalDisplay = ({
  decimalPlaces = 2,
  defaultValue,
  id,
  placeholder,
  validators
}: NumericalDisplayProps) => {
  const { control } = useFormContext()
  const [focused, setFocused] = useState(null)
  const { describedBy, inputId } = getFieldIds(id)

  const input = ({ display, numerical }: NumericalDisplayValue) => {
    if (focused) return display

    return numerical === undefined || !display
      ? ''
      : numerical.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimalPlaces
        })
  }
  const output = (value: string, current: NumericalDisplayValue): NumericalDisplayValue => {
    const [integer, decimal] = value.split('.')
    if (decimal && decimal.length > 2) {
      value = `${integer}.${decimal.substring(0, decimalPlaces)}`
    }
    const num = Number(value)
    return Number.isNaN(num)
      ? current
      : {
          numerical: num,
          display: value
        }
  }

  return (
    <Controller
      control={control}
      defaultValue={{ numerical: defaultValue, display: defaultValue?.toString() ?? '' }}
      name={id}
      render={({ field }) => (
        <Input
          {...field}
          aria-describedby={describedBy}
          className={classes.input}
          id={inputId}
          placeholder={placeholder}
          value={input(field.value)}
          onBlur={() => {
            setFocused(false)
            field.onBlur()
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            field.onChange(output(e.target.value, field.value))
          }
          onFocus={() => setFocused(true)}
        />
      )}
      rules={validators}
    />
  )
}

export const NumericalDisplayField = ({
  className,
  decimalPlaces = 2,
  ...rest
}: NumericalDisplayFieldProps) => (
  <div className={clsx(classes.root, className)}>
    <NumericalDisplay decimalPlaces={decimalPlaces} {...rest} />
  </div>
)
